import { useEffect, useState } from 'react';
import { Box, Flex, Heading, HStack, Text, VStack } from '@chakra-ui/react';
import { MdClose, MdDownload, MdIosShare } from 'react-icons/md';
import { RiftboundButton } from './ui/button';
import { useInstallPrompt } from '../hooks/useInstallPrompt';
import { MOBILE_TAB_BAR_HEIGHT } from './layout/MobileBottomNav';
import { useLocation } from 'react-router-dom';
import { getRouteChrome } from './layout/routeChrome';

/** Delay after mount before showing the banner, so it doesn't interrupt first interaction. */
import { devQuery, IS_DEV_BUILD } from '../constants/devControls';

const SHOW_DELAY_MS = devQuery.pwaPreview() ? 0 : IS_DEV_BUILD ? 500 : 4000;

export function PwaInstallBanner() {
  const { canInstall, isIos, isInstalled, isSnoozed, dismissCount, install, dismiss } = useInstallPrompt();
  const [visible, setVisible] = useState(false);
  const location = useLocation();

  const hasBottomNav = getRouteChrome(location.pathname).showMobileTabs;
  const bottomOffset = hasBottomNav
    ? `calc(${MOBILE_TAB_BAR_HEIGHT}px + env(safe-area-inset-bottom, 0px) + 8px)`
    : 'calc(env(safe-area-inset-bottom, 0px) + 8px)';

  useEffect(() => {
    if (isInstalled || isSnoozed) return;
    if (!canInstall && !isIos) return;
    const t = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    return () => clearTimeout(t);
  }, [canInstall, isIos, isInstalled, isSnoozed]);

  if (!visible || isInstalled || isSnoozed || (!canInstall && !isIos)) return null;

  // Second appearance — last chance copy
  const isLastChance = dismissCount === 1;

  return (
    <Box
      display={{ base: 'block', md: 'none' }}
      position="fixed"
      bottom={bottomOffset}
      left={3}
      right={3}
      zIndex={1200}
      bg="bgElevated"
      borderWidth="hairline"
      borderColor="border"
      borderRadius="xl"
      boxShadow="0 4px 24px rgba(0,0,0,0.5)"
      overflow="hidden"
      style={{ backdropFilter: 'blur(16px)' }}
    >
      {/* Top row: dismiss (left) + icon + text */}
      <Flex align="center" gap={2.5} px={2} pt={3} pb={canInstall ? 2 : 3}>
        <Box
          as="button"
          display="flex"
          alignItems="center"
          justifyContent="center"
          w={7}
          h={7}
          flexShrink={0}
          borderRadius="md"
          color="textMuted"
          _hover={{ bg: 'whiteAlpha.100', color: 'text' }}
          cursor="pointer"
          onClick={dismiss}
          aria-label="Dismiss install banner"
        >
          <MdClose size={16} />
        </Box>

        <img
          src="/icon-192x192.png"
          alt=""
          width={42}
          height={42}
          style={{ borderRadius: 8, flexShrink: 0 }}
        />

        <VStack align="flex-start" gap={1} flex={1} minW={0}>
          <Heading fontWeight="semibold" fontSize="md" lineClamp={1} lineHeight={1}>
            {isLastChance ? 'Last chance to install' : 'Install Riftbound DB'}
          </Heading>
          {isIos ? (
            <HStack gap={1} flexWrap="wrap">
              <Text fontSize="xs" color="textMuted">Tap</Text>
              <Box color="accent" display="inline-flex" alignItems="center">
                <MdIosShare size={14} />
              </Box>
              <Text fontSize="xs" color="textMuted">then "Add to Home Screen"</Text>
            </HStack>
          ) : (
            <Text fontSize="xs" color="textMuted">
              {isLastChance ? "We won't ask again after this" : 'Get the full app experience'}
            </Text>
          )}
        </VStack>
      </Flex>

      {/* Install button — full-width bottom row, clear of the bottom-right FAB */}
      {canInstall && (
        <Box px={3} pb={3}>
          <RiftboundButton
            treatment="primary"
            size="sm"
            w="full"
            onClick={() => { install(); dismiss(); }}
            gap={1.5}
          >
            <MdDownload />
            Install app
          </RiftboundButton>
        </Box>
      )}
    </Box>
  );
}
